<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
// import { Auth } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";

let itemObj = reactive({
  itemId: "",
  itemName: "",
  itemDescription: "",
  uneditedItemName: "",
  uneditedItemDescription: "",
  timestamp: "",
  latestEditor: "",
});

const currentStep = ref(1);

const showWarningDialog = ref(false);
const showDeleteDialog = ref(false);
const showDeleteSuccessDialog = ref(false);
const showDeleteFailedDialog = ref(false);
const showCompletionDialog = ref(false);
const showCloneCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);
const showSubmissionDialog = ref(false);
const showCompletionNameConflictDialog = ref(false);

var search = ref("");
var jsonPayload = ref({});

var showCloneDialog = ref(false);
var cloneItemName = ref("");
var cloneItemDescription = ref("");

const expanded = ref([]);

var loading = ref(false);
var deleting = ref(false);
var saving = ref(false);

var itemArray = ref([]);

var showCreationDialog = ref(false);

var selected = ref([]);
var selectedArray = ref([]);

var selectedScope = ref(["All"]);
var selectedType = ref(["All"]);

var scopeOptions = ref([
  "All",
  "Intune",
  "Windows",
  "macOS",
  "iOS-iPadOS",
  "Android",
  "Linux",
]);

var typeOptions = ref([
  "All",
  "AppCategories",
  "Applications",
  "AppProtectionPolicies",
  "AssignmentFilters",
  "DeploymentProfiles",
  "DeviceCompliance",
  "DeviceConfiguration",
  "DeviceEnrolmentConfigurations",
  "EndpointSecurity",
  "GroupPolicyConfiguration",
  "Scripts",
  "SettingsCategories",
]);

const steps = [
  { title: "Select Item", value: 1, complete: false },
  { title: "View/Edit Item", value: 2, complete: false },
];

const itemHeaders = [
  { title: "Name", value: "name", sortable: true, width: "40%" },
  {
    title: "Exported/Last Edited By",
    value: "latestEditor",
    sortable: true,
    width: "30%",
  },
  { title: "Type", value: "type", sortable: true, width: "20%" },
  { title: "Scope", value: "scope", sortable: true, width: "10%" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: 100, title: "100" },
  { value: -1, title: "All" },
];

onMounted(async () => {
  await getData();
});

async function getData() {
  loading.value = true;

  let items = await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "getItems",
  });

  items.forEach((item) => {
    /* Add a unique id to each item, this is to be the index for transitions between pages.
    unfortuantely this was the only way to acheive this as the MongoDB _id is not usable for this purpose. */
    item.id = Math.floor(Math.random() * 10000000000000000000);
    itemArray.value.push(item);
  });

  loading.value = false;
}

async function nextStep() {
  if (currentStep.value < steps.length) {
    // Mark the current step as complete.
    steps[currentStep.value - 1].complete = true;

    switch (currentStep.value) {
      case 1:
        if (currentStep.value == 1 && selected.value.length == 0) {
          // If the user has not selected a collection, show the warning dialog box.
          selectionWarningDialogBox(true);
        } else {
          // Get the details of the selected item into itemObj.
          getItemDetails();

          // Get the JSON payload of the selected item from GitHub.
          await getItemPayload();

          // Move to the next step.
          currentStep.value++;
        }
        break;
      case 2:
        if (
          currentStep.value == 2 &&
          getSelectedItemsDetails.value.length == 0
        ) {
          // If the user has not selected any items, show the warning dialog box.
          selectionWarningDialogBox(true);
        } else {
          // Move to the next step.
          currentStep.value++;
        }
        break;
      default:
        // Do nothing
        break;
    }
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    // Mark the current step as incomplete if they go back.
    steps[currentStep.value - 1].complete = false;
  }
  // Clear the selected items if the user goes back to the first page.
  if (currentStep.value == 1) {
    itemObj = {};
    selected.value = [];
  }
}

function getItemDetails() {
  // Populate the itemObj with the details of the selected item.
  var id = selected.value[0];

  const selectedItem = itemArray.value.find((item) => item.id === id);
  itemObj.itemName = selectedItem.name;
  itemObj.itemDescription = selectedItem.description;
  itemObj.itemId = selectedItem._id;
  itemObj.uneditedItemName = selectedItem.name;
  itemObj.uneditedItemDescription = selectedItem.description;
  itemObj.timestamp = convertTimestamp(selectedItem.timestamp);
  if (selectedItem.latestEditor == null) {
    itemObj.latestEditor = "N/A";
  } else {
    itemObj.latestEditor = selectedItem.latestEditor;
  }
}

const getSelectedItemsDetails = computed(() => {
  const uniqueItemIds = new Set();
  return selected.value
    .map((id) => {
      /* When transitioning between steps, only the id of the selected items are passed,
      this function references the id and collects the rest of the missing data for the new page
      It also removes duplicate items just in case the user switches back and forth between the pages. */
      const selectedItem = itemArray.value.find((item) => item.id === id);
      if (selectedItem && !uniqueItemIds.has(id)) {
        uniqueItemIds.add(id);
        const { _id, name, description, scope, type, latestEditor } =
          selectedItem;
        const newItem = {
          _id,
          name,
          description,
          scope,
          type,
          latestEditor,
        };
        return newItem;
      }
    })
    .filter(Boolean);
});

const filteredItems = computed(() => {
  // If both selectedScope and selectedType are set to 'All', return all items.
  if (selectedScope.value[0] === "All" && selectedType.value[0] === "All") {
    return itemArray.value;
  }

  // Otherwise, filter based on selectedScope and selectedType.
  return itemArray.value.filter((item) => {
    const passesScope =
      selectedScope.value[0] === "All" ||
      selectedScope.value.includes(item.scope);
    const passesType =
      selectedType.value[0] === "All" || selectedType.value.includes(item.type);

    return passesScope && passesType;
  });
});

function changeStatusColour(status) {
  switch (status) {
    case "No change":
      return "grey";
    case "Add":
      return "green";
    case "Remove":
      return "red";
    default:
      return "grey";
  }
}

function creationDialogBox(status) {
  if (status == true) {
    showCreationDialog.value = true;
  } else {
    showCreationDialog.value = false;
  }
}

function selectionWarningDialogBox(status) {
  if (status == true) {
    showWarningDialog.value = true;
  } else {
    showWarningDialog.value = false;
  }
}

function deleteDialogBox(status) {
  if (status == true) {
    showDeleteDialog.value = true;
  } else {
    showDeleteDialog.value = false;
  }
}

function cloneDialogBox(status) {
  if (status == true) {
    showCloneDialog.value = true;
  } else {
    showCloneDialog.value = false;
  }
}

function deleteSuccessDialogBox(status) {
  if (status == true) {
    showDeleteSuccessDialog.value = true;
  } else {
    showDeleteSuccessDialog.value = false;
  }
}

function completionNameConflictDialogBox(status) {
  if (status == true) {
    showCompletionNameConflictDialog.value = true;
  } else {
    showCompletionNameConflictDialog.value = false;
  }
}

function deleteFailedDialogBox(status) {
  if (status == true) {
    showDeleteFailedDialog.value = true;
  } else {
    showDeleteFailedDialog.value = false;
  }
}

function completionDialogBox(status) {
  if (status == true) {
    showCompletionDialog.value = true;
  } else {
    showCompletionDialog.value = false;
  }
}

function cloneCompletionDialogBox(status) {
  if (status == true) {
    showCloneCompletionDialog.value = true;
  } else {
    showCloneCompletionDialog.value = false;
  }
}

function submissionDialogBox(status) {
  if (status == true) {
    showSubmissionDialog.value = true;
  } else {
    showSubmissionDialog.value = false;
  }
}

function completionWarningDialogBox(status) {
  if (status == true) {
    showCompletionWarningDialog.value = true;
  } else {
    showCompletionWarningDialog.value = false;
  }
}

function createCollectionDialogBox(status) {
  if (status == true) {
    showCreationDialog.value = true;
  } else {
    showCreationDialog.value = false;
  }
}

async function getPortalUser() {
  // Get the ELA authenticated user's email address.
  const currentAuthenticatedUser = await fetchAuthSession();
  if (currentAuthenticatedUser) {
    var email = currentAuthenticatedUser.tokens.idToken.payload.email;
  }

  return email;
}

async function sendLog(logType) {
  var portalUser = await getPortalUser();

  var selectedItems = getSelectedItemsDetails.value;

  var log = {};
  var type;
  var logObject = {};

  // Check if the user is creating a new collection or editing an existing one.
  switch (logType) {
    case "edit":
      type = "Edit";

      // Build the log object.
      logObject = {
        mdm: "Intune",
        type: "ItemEditor",
        portalUser: portalUser,
        timestamp: new Date().toISOString(),
        log: (log = {
          itemId: itemObj.itemId,
          itemName: itemObj.itemName,
          itemState: type,
          before: {
            name: itemObj.uneditedItemName,
            description: itemObj.uneditedItemDescription,
          },
          after: {
            name: itemObj.itemName,
            description: itemObj.itemDescription,
          },
        }),
      };

      break;
    case "delete":
      type = "Delete";

      // Build the log object.
      logObject = {
        mdm: "Intune",
        logType: "ItemEditor",
        portalUser: portalUser,
        timestamp: new Date().toISOString(),
        log: (log = {
          itemId: itemObj.itemId,
          itemName: itemObj.name,
          itemState: selectedItems,
        }),
      };

      break;
    default:
      // Do nothing
      break;
  }

  // Send the log object to the API.
  await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "log",
    payload: logObject,
  });
}

function resetWizard() {
  // Following the successful submission of a collection, reset the wizard.

  // Close any dialog boxes.
  completionDialogBox(false);
  deleteSuccessDialogBox(false);
  completionWarningDialogBox(false);
  // Set the page to 1
  currentStep.value = 1;
  // Clear any selected items so they can be reloaded.
  itemArray.value = [];
  selected.value = [];
  // Set any completed steps to false (for the v-stepper-header).
  steps.forEach((step) => {
    step.complete = false;
  });
  itemObj = {};
  // Reload new items and collections.
  getData();
}

async function submitChanges() {
  // Close the submission dialog box.
  showSubmissionDialog.value = false;

  saving.value = true;

  var portalUser = await getPortalUser();

  var items = [];
  var itemPayload = {};
  var type = "";

  // Build the payload for the API call.
  itemPayload = {
    _id: itemObj.itemId,
    oldName: itemObj.uneditedItemName,
    name: itemObj.itemName,
    description: itemObj.itemDescription,
    latestEditor: portalUser,
  };

  try {
    // Send the payload to the API.
    let result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "intune",
      command: "editItem",
      payload: itemPayload,
    });

    saving.value = false;

    if (result == "Success") {
      // Send the log to the API.
      await sendLog("edit");

      completionDialogBox(true);
    } else if (result == "Name conflict") {
      completionNameConflictDialogBox(true);
    } else {
      completionWarningDialogBox(true);
    }
  } catch (error) {
    console.log(error);
    completionWarningDialogBox(true);
  }
  saving.value = false;
}

const downloadJson = () => {
  const blob = new Blob([jsonPayload], {
    type: "application/json",
  });
  const link = document.createElement("a");

  link.href = URL.createObjectURL(blob);
  link.download = itemObj.itemName + ".json";

  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
};

async function getItemPayload() {
  loading.value = true;
  let payload = await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "getItemPayload",
    payload: itemObj.itemId,
  });

  jsonPayload = JSON.stringify(payload, null, "    ");
  loading.value = false;
}

async function deleteItems(seleted) {
  // Close the delete dialog box.
  showDeleteDialog.value = false;

  deleting.value = true;

  // Get selected item details.
  var selectedItems = getSelectedItemsDetails.value;

  // Send the id to the API to delete the collection.
  try {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "intune",
      command: "deleteItems",
      payload: selectedItems,
    });

    if (result == "Success") {
      showDeleteSuccessDialog.value = true;
      deleting.value = false;

      // Send the log to the API.
      await sendLog("delete");
    } else {
      deleting.value = false;
      showDeleteFailedDialog.value = true;
    }
  } catch (error) {
    console.log(error);
    deleting.value = false;
    showDeleteFailedDialog.value = true;
  }
}

function convertTimestamp(dateString) {
  const date = new Date(dateString);
  const options = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  };
  const formattedDate = date.toLocaleDateString("en-US", options);
  console.log(formattedDate);
  return formattedDate;
}

async function createClonedItem() {
  showCloneDialog.value = false;

  var itemPayload = {};

  var portalUser = await getPortalUser();

  // Build the payload for the API call.
  itemPayload = {
    _id: itemObj.itemId,
    name: cloneItemName.value,
    description: cloneItemDescription.value,
    latestEditor: portalUser,
  };

  try {
    // Send the payload to the API.
    let result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "intune",
      command: "cloneItem",
      payload: itemPayload,
    });

    if (result == "Success") {
      completionDialogBox(true);
    } else if (result == "Name conflict") {
      completionNameConflictDialogBox(true);
    } else {
      completionWarningDialogBox(true);
    }
  } catch (error) {
    console.log(error);
    completionWarningDialogBox(true);
  }

  // Reset variables.
  cloneItemName = "";
  cloneItemDescription = "";
}
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :subtitle="step.subtitle"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <template v-if="step.value === 1">
            <h2>Select a configuration item to edit or delete</h2>
            <v-responsive>
              <div class="d-flex">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 400px"
                ></v-text-field>
                <v-select
                  v-model="selectedType"
                  :items="typeOptions"
                  chips
                  multiple
                  label="Type"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 20px"
                ></v-select>
                <v-select
                  v-model="selectedScope"
                  :items="scopeOptions"
                  chips
                  multiple
                  label="Scope"
                  variant="underlined"
                  class="input-field"
                ></v-select>
              </div>
            </v-responsive>
            <v-data-table
              :headers="itemHeaders"
              :items="filteredItems"
              :search="search"
              item-key="id"
              show-select
              v-model="selected"
              class="elevation-2"
              show-expand
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              no-data-text="No items available"
            >
              <template v-slot:expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <table class="expanded-row">
                      <thead>
                        <tr>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {{
                              item.description
                                ? item.description
                                : "No Description"
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>
          <template v-if="step.value === 2">
            <h2>Editing item - {{ itemObj.itemName }}</h2>
            <v-card-text
              ><v-card-text class="text-center">
                Change the item name or description where required and click
                'Save Changes' to update, or click 'Delete Item' to delete.
                Please note - Changing the name and/or description will update
                the JSON fields in the payload when saved, and thus will be
                viewable in the destination tenant.</v-card-text
              >
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="itemObj.itemName"
                    label="Item Name"
                    required
                    variant="outlined"
                  ></v-text-field>
                  <v-textarea
                    v-model="itemObj.itemDescription"
                    label="Description"
                    required
                    rows="8"
                    variant="outlined"
                    no-resize
                  ></v-textarea>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="itemObj.timestamp"
                        label="Last Updated"
                        variant="outlined"
                        readonly
                      ></v-text-field>
                      <v-text-field
                        v-model="itemObj.latestEditor"
                        label="Created/Last Edited By"
                        variant="outlined"
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <v-textarea
                    variant="outlined"
                    label="JSON Payload"
                    v-model="jsonPayload"
                    readonly
                    rows="20"
                    no-resize
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
            >Back</v-btn
          >
          <v-btn
            v-if="currentStep == 2"
            variant="elevated"
            color="primary"
            @click="cloneDialogBox(true)"
            >Clone Item</v-btn
          >
          <v-btn
            v-if="currentStep == 2"
            variant="elevated"
            color="primary"
            @click="deleteDialogBox(true)"
            >Delete Item</v-btn
          >
          <v-btn
            v-if="currentStep == 2"
            variant="elevated"
            color="primary"
            @click="downloadJson"
            >Download JSON</v-btn
          >
          <v-btn
            v-if="currentStep == 1 && selected.length > 1"
            variant="elevated"
            color="primary"
            @click="deleteDialogBox(true)"
            class="ml-auto"
            >Delete Items</v-btn
          >
          <v-btn
            v-if="currentStep == 1 && selected.length < 2"
            variant="elevated"
            color="primary"
            @click="nextStep"
            class="ml-auto"
            >Next</v-btn
          >
          <v-btn
            v-if="currentStep == 2"
            @click="submissionDialogBox(true)"
            variant="elevated"
            color="primary"
            class="ml-auto"
            :disabled="!itemObj.itemName || !itemObj.itemDescription"
            >Save Changes</v-btn
          >
        </v-card-actions>
      </v-sheet>
      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>Please select an item before proceeding</h3></v-card-text
            >

            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="selectionWarningDialogBox"
                >OK</v-btn
              ></v-card-actions
            >
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>
                You are about to delete this item or items, please confirm
              </h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="deleteDialogBox(false)">Cancel</v-btn>
              <v-btn @click="deleteItems" class="flex-grow-1" variant="tonal"
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showDeleteSuccessDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text><h3>Item or items have been deleted</h3></v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn class="flex-grow-1" variant="tonal" @click="resetWizard"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showDeleteFailedDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>Deletion of selected item has failed</h3></v-card-text
            >
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="deleteFailedDialogBox(false)"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Item Updated</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="resetWizard"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showCloneCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Item Cloned</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="cloneCompletionDialogBox(false)"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showSubmissionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>
                You are about to update the selected item. Are you sure you want
                to proceed?
              </h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="submissionDialogBox">Cancel</v-btn>
              <v-btn @click="submitChanges" class="flex-grow-1" variant="tonal"
                >Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="red"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Error submitting item</div>
            </div>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="resetWizard"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog
        v-model="showCompletionNameConflictDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="red"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="font-weight-bold">
                Item name already exists, please rename and try again
              </div>
            </div>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="completionNameConflictDialogBox"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showCloneDialog" persistent>
        <v-card min-width="600" variant="elevated" class="mx-auto">
          <v-card-title>Enter the item details</v-card-title>
          <v-card-text>
            <v-table class="text-left">
              <v-responsive class="mx-auto py-2" max-width="600">
                <v-text-field
                  clearable
                  v-model="cloneItemName"
                  label="Name"
                  type="input"
                  hint="Enter a new item name"
                  variant="outlined"
                ></v-text-field>
              </v-responsive>
              <v-responsive class="mx-auto py-2" max-width="600">
                <v-text-field
                  clearable
                  v-model="cloneItemDescription"
                  label="Description"
                  type="input"
                  hint="Enter a new description for the item"
                  variant="outlined"
                ></v-text-field>
              </v-responsive>
              <div class="text-center">
                <v-card>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn @click="cloneDialogBox(false)">Cancel</v-btn>
                    <v-btn
                      @click="createClonedItem"
                      class="flex-grow-1"
                      variant="tonal"
                      :disabled="!cloneItemName || !cloneItemDescription"
                      >Clone</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </div>
            </v-table>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="loading" persistent width="auto">
        <v-card color="primary">
          <v-card-text>
            Loading data, please wait...
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="deleting" persistent width="auto">
        <v-card color="primary">
          <v-card-text>
            Deleting items, please wait...
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="saving" persistent width="auto">
        <v-card color="primary">
          <v-card-text>
            Saving changes, please wait...
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
/* Required for the scope filter to work correctly. */
/* tbody tr:nth-of-type(odd) {
  background-color: rgb(247, 247, 247);
} */

/* tbody tr:nth-of-type(even) {
  background-color: ;
} */
.expanded-row {
  width: 100%;
  border-collapse: collapse;
}
.expanded-row th {
  background-color: rgb(210, 210, 210);
  border: 1px solid #a4a4a4;
}
.expanded-row td {
  padding: 1px;
  border: 1px solid #a4a4a4;
  /* background-color: rgb(247, 247, 247); */
}
/* Control the width of the columns in the expanded row. */
.expanded-row td:nth-child(1) {
  width: 20%;
}
.expanded-row td:nth-child(2) {
  width: 60%;
}
.expanded-row td:nth-child(3) {
  width: 10%;
  text-align: center;
}
.expanded-row td:nth-child(4) {
  width: 10%;
  text-align: center;
}
</style>
